import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { launchPlanStep, ticketPlanQuery, askAdamPlan } from '../api/desk'

export function TicketPlanPanel({ id }: { id: number }) {
  const ticketId = id
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({ ...ticketPlanQuery(ticketId), enabled: ticketId > 0, refetchInterval: 15000 })
  const [armed, setArmed] = useState<number | null>(null)
  const launch = useMutation({
    mutationFn: ({ stepId, body }: { stepId: number; body: Record<string, unknown> }) => launchPlanStep(ticketId, stepId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['easeedesk', 'ticket', ticketId] }),
  })

  if (isLoading || !data) return <p className="text-muted">Loading plan…</p>
  const onParent = data.on_parent as { ticket_id: number; plan_id: number } | undefined
  if (onParent) {
    return <p>The plan for this incident is on ticket #{onParent.ticket_id}.</p>
  }
  const plan = data.plan as { title?: string; state?: string; steps?: Array<Record<string, unknown>>; ask_adam_at?: string } | null
  if (!plan) return <p className="text-muted">No remediation plan on this ticket.</p>
  const canLaunch = Boolean(data.can_launch)
  const canFull = Boolean(data.can_full)

  return (
    <div className="ticket-plan-panel">
      <h4>{plan.title} <small className="text-muted">({plan.state})</small></h4>
      <ol className="list-unstyled">
        {(plan.steps ?? []).map((step) => {
          const stepId = Number(step.id ?? step.ID ?? 0)
          const kind = String(step.kind ?? '')
          return (
            <li key={stepId} className="panel panel-default" style={{ marginBottom: 12, padding: 12 }}>
              <strong>{String(step.title ?? '')}</strong> <span className="label label-default">{kind}</span>
              <div className="text-muted">{String(step.why ?? '')}</div>
              {kind === 'command_suggested' && canFull && <pre>{String(step.command_text ?? '')}</pre>}
              {kind === 'command_suggested' && !canFull && <p className="text-muted">[shown only with Full Access]</p>}
              {canLaunch && kind !== 'manual' && kind !== 'verify' && plan.state !== 'superseded' && plan.state !== 'abandoned' && (
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => {
                    if (armed !== stepId) {
                      setArmed(stepId)
                      return
                    }
                    launch.mutate({ stepId, body: { confirm: true, ...(kind === 'command_suggested' ? { agree: 'I read it' } : {}) } })
                    setArmed(null)
                  }}
                >
                  {armed === stepId ? 'Sure? run now' : 'Run this step'}
                </button>
              )}
            </li>
          )
        })}
      </ol>
      {canLaunch && !plan.ask_adam_at && (
        <button type="button" className="btn btn-default" onClick={() => askAdamPlan(ticketId).then(() => qc.invalidateQueries({ queryKey: ['easeedesk', 'ticket', ticketId] }))}>
          Ask Adam to continue
        </button>
      )}
    </div>
  )
}
